import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLang } from '../../contexts/LanguageContext';
import { ChevronLeft, Plus, Edit, Trash2, MessageCircle, Star } from 'lucide-react';
import { EMZI_COLORS, EMZI_SHADOWS, EMZI_RADIUS } from '../../styles/emziCI';
import toast from '../../utils/toast';
import { secureStorage } from '../../utils/secureStorage';
import { BACKEND_URL } from '../../utils/backendUrl';

/**
 * EMZI NEXUS V6.3 - WhatsApp Templates Management Page
 * Admin interface to manage WhatsApp message templates
 */

const WhatsAppTemplatesPage = () => {
  const navigate = useNavigate();
  const t = useLang();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [filter, setFilter] = useState('all'); // all, welcome, announcement, reminder, general

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
            const response = await fetch(
        `${BACKEND_URL}/api/admin-panel/whatsapp-templates?active_only=true`,
        {
          headers: {
            'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      } else {
        toast.error('Failed to load templates');
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('⚠️ PERMANENT DELETE\n\nAre you sure you want to permanently delete this template?\n\nThis action CANNOT be undone!')) {
      return;
    }

    try {
            const response = await fetch(
        `${BACKEND_URL}/api/admin-panel/whatsapp-templates/${templateId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`
          }
        }
      );

      if (response.ok) {
        toast.success('Template permanently deleted');
        fetchTemplates();
      } else {
        toast.error('Failed to delete template');
      }
    } catch (err) {
      console.error('Error deleting template:', err);
      toast.error('Failed to delete template');
    }
  };

  const filteredTemplates = templates.filter(t => {
    if (filter === 'all') return true;
    return t.category === filter;
  });

  const getCategoryColor = (category) => {
    switch (category) {
      case 'welcome': return '#10b981';
      case 'announcement': return '#f59e0b';
      case 'reminder': return '#3b82f6';
      case 'general': return '#8b5cf6';
      default: return EMZI_COLORS.textSecondary;
    }
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case 'welcome': return 'Welcome';
      case 'announcement': return 'Announcement';
      case 'reminder': return 'Reminder';
      case 'general': return 'General';
      default: return category;
    }
  };

  return (
    <div style={{
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      background: EMZI_COLORS.background
    }}>
      {/* Fixed Header - EMZI CI Standard */}
      <div style={{
        background: EMZI_COLORS.primary,
        padding: '20px',
        color: 'white',
        boxShadow: EMZI_SHADOWS.header,
        flexShrink: 0
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '8px'
        }}>
          <button
            onClick={() => navigate('/admin')}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              padding: '8px',
              borderRadius: '50%',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <ChevronLeft size={20} color="white" />
          </button>
          <div>
            <h1 style={{
              fontSize: '24px',
              fontWeight: '700',
              margin: 0
            }}>
              WhatsApp Templates
            </h1>
            <p style={{
              fontSize: '14px',
              opacity: 0.9,
              margin: '4px 0 0 0'
            }}>
              Manage message templates
            </p>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
        scrollBehavior: 'smooth'
      }}>
        {/* Stats Bar */}
        <div style={{
          display: 'flex',
          gap: '12px',
          padding: '20px',
          overflowX: 'auto'
        }}>
        <div style={{
          flex: '1',
          minWidth: '140px',
          background: 'white',
          padding: '16px',
          borderRadius: EMZI_RADIUS.md,
          boxShadow: EMZI_SHADOWS.sm
        }}>
          <div style={{
            fontSize: '12px',
            color: EMZI_COLORS.textSecondary,
            marginBottom: '4px'
          }}>
            Total Templates
          </div>
          <div style={{
            fontSize: '28px',
            fontWeight: '700',
            color: EMZI_COLORS.primary
          }}>
            {templates.length}
          </div>
        </div>
        <div style={{
          flex: '1',
          minWidth: '140px',
          background: 'white',
          padding: '16px',
          borderRadius: EMZI_RADIUS.md,
          boxShadow: EMZI_SHADOWS.sm
        }}>
          <div style={{
            fontSize: '12px',
            color: EMZI_COLORS.textSecondary,
            marginBottom: '4px'
          }}>
            Active
          </div>
          <div style={{
            fontSize: '28px',
            fontWeight: '700',
            color: '#10b981'
          }}>
            {templates.filter(t => t.is_active).length}
          </div>
        </div>
      </div>

      {/* Filters and Create Button */}
      <div style={{
        padding: '0 20px 16px',
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
        flexWrap: 'wrap'
      }}>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{
            flex: '1',
            minWidth: '150px',
            padding: '10px 12px',
            border: `2px solid ${EMZI_COLORS.border}`,
            borderRadius: EMZI_RADIUS.md,
            fontSize: '14px',
            fontWeight: '500',
            background: 'white'
          }}
        >
          <option value="all">All Categories</option>
          <option value="welcome">Welcome</option>
          <option value="announcement">Announcement</option>
          <option value="reminder">Reminder</option>
          <option value="general">General</option>
        </select>
        <button
          onClick={() => {
            setEditingTemplate(null);
            setShowCreateModal(true);
          }}
          style={{
            padding: '10px 20px',
            background: EMZI_COLORS.primary,
            color: 'white',
            border: 'none',
            borderRadius: EMZI_RADIUS.md,
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            minHeight: '44px',
            boxShadow: EMZI_SHADOWS.sm
          }}
        >
          <Plus size={18} />
          Create Template
        </button>
      </div>

      {/* Templates List - Scrollable Container */}
      <div style={{
        padding: '0 20px 20px',
        maxHeight: 'calc(100vh - 420px)',
        overflowY: 'auto'
      }}>
        <div style={{
          background: 'white',
          borderRadius: EMZI_RADIUS.lg,
          padding: '16px',
          boxShadow: EMZI_SHADOWS.sm,
          border: `1px solid ${EMZI_COLORS.border}`
        }}>
          {loading ? (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: EMZI_COLORS.textSecondary
            }}>
              Loading templates...
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px'
            }}>
              <MessageCircle size={48} color={EMZI_COLORS.textSecondary} style={{ marginBottom: '16px' }} />
              <p style={{ color: EMZI_COLORS.textSecondary, margin: 0 }}>
                No templates found
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {filteredTemplates.map(template => (
              <div
                key={template.template_id}
                style={{
                  background: 'white',
                  borderRadius: EMZI_RADIUS.lg,
                  padding: '16px',
                  boxShadow: EMZI_SHADOWS.sm,
                  border: template.is_default ? `2px solid ${getCategoryColor(template.category)}` : '1px solid ' + EMZI_COLORS.border
                }}
              >
                {/* Header */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '12px'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '4px'
                    }}>
                      <h3 style={{
                        fontSize: '16px',
                        fontWeight: '700',
                        color: EMZI_COLORS.textPrimary,
                        margin: 0
                      }}>
                        {template.name}
                      </h3>
                      {template.is_default && (
                        <Star size={16} fill={getCategoryColor(template.category)} color={getCategoryColor(template.category)} />
                      )}
                    </div>
                    <div style={{
                      display: 'inline-block',
                      padding: '4px 8px',
                      background: getCategoryColor(template.category) + '20',
                      color: getCategoryColor(template.category),
                      borderRadius: EMZI_RADIUS.sm,
                      fontSize: '12px',
                      fontWeight: '600'
                    }}>
                      {getCategoryLabel(template.category)}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => {
                        setEditingTemplate(template);
                        setShowCreateModal(true);
                      }}
                      style={{
                        padding: '8px',
                        background: EMZI_COLORS.surface,
                        border: 'none',
                        borderRadius: EMZI_RADIUS.sm,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <Edit size={16} color={EMZI_COLORS.primary} />
                    </button>
                    <button
                      onClick={() => handleDelete(template.template_id)}
                      style={{
                        padding: '8px',
                        background: '#fee2e2',
                        border: 'none',
                        borderRadius: EMZI_RADIUS.sm,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <Trash2 size={16} color="#dc2626" />
                    </button>
                  </div>
                </div>

                {/* Message Preview */}
                <div style={{
                  background: EMZI_COLORS.surface,
                  padding: '12px',
                  borderRadius: EMZI_RADIUS.md,
                  fontSize: '13px',
                  fontFamily: 'monospace',
                  color: EMZI_COLORS.textPrimary,
                  whiteSpace: 'pre-wrap',
                  lineHeight: '1.6',
                  maxHeight: '120px',
                  overflow: 'auto'
                }}>
                  {template.message_body}
                </div>

                {/* Footer */}
                <div style={{
                  marginTop: '12px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: '12px',
                  color: EMZI_COLORS.textSecondary
                }}>
                  <div>
                    {template.is_default && (
                      <span style={{ fontWeight: '600', color: getCategoryColor(template.category) }}>
                        Default Template
                      </span>
                    )}
                  </div>
                  <div>
                    Status: <span style={{
                      fontWeight: '600',
                      color: template.is_active ? '#10b981' : '#ef4444'
                    }}>
                      {template.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
            </div>
          )}
        </div>
        <div style={{ paddingBottom: '100px' }}></div> {/* Bottom spacing */}
      </div>
      
      </div> {/* End Scrollable Content */}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <TemplateModal
          isOpen={showCreateModal}
          onClose={() => {
            setShowCreateModal(false);
            setEditingTemplate(null);
          }}
          template={editingTemplate}
          onSuccess={() => {
            fetchTemplates();
            setShowCreateModal(false);
            setEditingTemplate(null);
          }}
        />
      )}
    </div>
  );
};

// Template Create/Edit Modal Component
const TemplateModal = ({ isOpen, onClose, template, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    category: 'general',
    message_body: '',
    is_default: false
  });
  const [loading, setLoading] = useState(false);
  const textareaRef = React.useRef(null);

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name || '',
        category: template.category || 'general',
        message_body: template.message_body || '',
        is_default: template.is_default || false
      });
    } else {
      setFormData({
        name: '',
        category: 'general',
        message_body: '',
        is_default: false
      });
    }
  }, [template]);

  // Insert placeholder at cursor position
  const insertPlaceholder = (placeholder) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = formData.message_body;
    const before = text.substring(0, start);
    const after = text.substring(end, text.length);

    // Insert placeholder at cursor position
    const newText = before + placeholder + after;
    setFormData({ ...formData, message_body: newText });

    // Set cursor position after the inserted placeholder
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + placeholder.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  // Available placeholders with descriptions
  const placeholders = [
    { text: '{name}', label: 'Name', color: '#3b82f6' },
    { text: '{email}', label: 'Email', color: '#8b5cf6' },
    { text: '{role}', label: 'Role', color: '#f59e0b' },
    { text: '{login_url}', label: 'Login URL', color: '#10b981' },
    { text: '{temp_password}', label: 'Password', color: '#ef4444' },
    { text: '{link_url}', label: 'Link', color: '#06b6d4' },
    { text: '{date}', label: 'Date', color: '#84cc16' },
    { text: '{time}', label: 'Time', color: '#a855f7' },
    { text: '{category}', label: 'Category', color: '#ec4899' },
    { text: '{custom_field}', label: 'Custom', color: '#6366f1' }
  ];

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!formData.name || !formData.message_body) {
      toast.error('Name and message are required');
      return;
    }

    try {
      setLoading(true);
            const url = template
        ? `${BACKEND_URL}/api/admin-panel/whatsapp-templates/${template.template_id}`
        : `${BACKEND_URL}/api/admin-panel/whatsapp-templates`;
      
      const response = await fetch(url, {
        method: template ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(template ? 'Template updated!' : 'Template created!');
        onSuccess();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save template');
      }
    } catch (err) {
      console.error('Error saving template:', err);
      toast.error('Failed to save template');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: EMZI_RADIUS.lg,
        maxWidth: '600px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: EMZI_SHADOWS.xl
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: `1px solid ${EMZI_COLORS.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <h2 style={{
            fontSize: '20px',
            fontWeight: '700',
            color: EMZI_COLORS.textPrimary,
            margin: 0
          }}>
            {template ? 'Edit Template' : 'Create Template'}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px'
            }}
          >
            <ChevronLeft size={24} color={EMZI_COLORS.textSecondary} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '24px' }}>
          {/* Name */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              color: EMZI_COLORS.textPrimary,
              marginBottom: '8px'
            }}>
              Template Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Welcome New User"
              style={{
                width: '100%',
                padding: '10px 12px',
                border: `2px solid ${EMZI_COLORS.border}`,
                borderRadius: EMZI_RADIUS.md,
                fontSize: '14px',
                outline: 'none'
              }}
            />
          </div>

          {/* Category */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              color: EMZI_COLORS.textPrimary,
              marginBottom: '8px'
            }}>
              Category *
            </label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: `2px solid ${EMZI_COLORS.border}`,
                borderRadius: EMZI_RADIUS.md,
                fontSize: '14px',
                background: 'white'
              }}
            >
              <option value="welcome">Welcome</option>
              <option value="announcement">Announcement</option>
              <option value="reminder">Reminder</option>
              <option value="general">General</option>
            </select>
          </div>

          {/* Message Body */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              color: EMZI_COLORS.textPrimary,
              marginBottom: '8px'
            }}>
              Message Body *
            </label>
            
            {/* Interactive Placeholder Chips */}
            <div style={{
              marginBottom: '12px',
              padding: '12px',
              background: EMZI_COLORS.surface,
              borderRadius: EMZI_RADIUS.md,
              border: `1px solid ${EMZI_COLORS.border}`
            }}>
              <div style={{
                fontSize: '12px',
                fontWeight: '600',
                color: EMZI_COLORS.textPrimary,
                marginBottom: '8px'
              }}>
                📌 Tap to Insert:
              </div>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '6px'
              }}>
                {placeholders.map((ph, index) => (
                  <button
                    key={`item-${index}`}
                    onClick={() => insertPlaceholder(ph.text)}
                    type="button"
                    style={{
                      padding: '6px 12px',
                      background: ph.color + '15',
                      color: ph.color,
                      border: `1px solid ${ph.color}40`,
                      borderRadius: EMZI_RADIUS.sm,
                      fontSize: '12px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      minHeight: '32px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = ph.color + '25';
                      e.currentTarget.style.transform = 'scale(1.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = ph.color + '15';
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                  >
                    <span style={{ fontSize: '10px' }}>{ph.label}</span>
                    <code style={{
                      fontSize: '10px',
                      fontFamily: 'monospace',
                      opacity: 0.8
                    }}>
                      {ph.text}
                    </code>
                  </button>
                ))}
              </div>
              <div style={{
                fontSize: '11px',
                color: EMZI_COLORS.textSecondary,
                marginTop: '8px',
                fontStyle: 'italic'
              }}>
                💡 Tip: Click any placeholder to insert it at your cursor position
              </div>
            </div>

            <textarea
              ref={textareaRef}
              value={formData.message_body}
              onChange={(e) => setFormData({ ...formData, message_body: e.target.value })}
              rows={10}
              placeholder="Hello {name},&#10;&#10;Welcome to EMZI NEXUS!"
              style={{
                width: '100%',
                padding: '12px',
                border: `2px solid ${EMZI_COLORS.border}`,
                borderRadius: EMZI_RADIUS.md,
                fontSize: '14px',
                fontFamily: 'monospace',
                resize: 'vertical',
                outline: 'none'
              }}
              onFocus={(e) => e.target.style.borderColor = EMZI_COLORS.primary}
              onBlur={(e) => e.target.style.borderColor = EMZI_COLORS.border}
            />
          </div>

          {/* Set as Default */}
          <div style={{
            marginBottom: '24px',
            padding: '12px',
            background: EMZI_COLORS.surface,
            borderRadius: EMZI_RADIUS.md
          }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={formData.is_default}
                onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <div>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: EMZI_COLORS.textPrimary
                }}>
                  Set as default template
                </div>
                <div style={{
                  fontSize: '12px',
                  color: EMZI_COLORS.textSecondary
                }}>
                  This will be auto-selected for this category
                </div>
              </div>
            </label>
          </div>

          {/* Actions */}
          <div style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end'
          }}>
            <button
              onClick={onClose}
              disabled={loading}
              style={{
                padding: '10px 20px',
                background: 'white',
                color: EMZI_COLORS.textPrimary,
                border: `2px solid ${EMZI_COLORS.border}`,
                borderRadius: EMZI_RADIUS.md,
                fontSize: '14px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                minHeight: '44px'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              style={{
                padding: '10px 20px',
                background: EMZI_COLORS.primary,
                color: 'white',
                border: 'none',
                borderRadius: EMZI_RADIUS.md,
                fontSize: '14px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                minHeight: '44px',
                opacity: loading ? 0.6 : 1
              }}
            >
              {loading ? 'Saving...' : (template ? 'Update' : 'Create')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppTemplatesPage;
